import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { supabase } from '../services/supabaseClient';

export interface MultiFlowPDFOptions {
  filename?: string;
  includeDate?: boolean;
  quality?: number;
  workspaces?: any[];
  onProgress?: (current: number, total: number, flowName: string) => void;
}

/**
 * Export multiple flows to a single PDF file using simple HTML rendering
 */
export async function exportMultipleFlowsToPDF(
  flows: any[],
  orgId: string,
  orgName: string,
  orgData: any,
  workspaces: any[],
  options: MultiFlowPDFOptions = {}
): Promise<void> {
  const {
    filename = `${orgName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-all-flows`,
    includeDate = true,
    quality = 0.95,
    onProgress
  } = options;

  // Create PDF document
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  let isFirstPage = true;

  // Create a progress modal
  const progressModal = createProgressModal();
  document.body.appendChild(progressModal.container);

  // Create a hidden container for rendering
  const renderContainer = document.createElement('div');
  renderContainer.id = 'pdf-render-container';
  renderContainer.style.cssText = `
    position: absolute;
    left: -9999px;
    top: 0;
    width: 1200px;
    background: white;
    padding: 40px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  `;
  document.body.appendChild(renderContainer);

  try {
    // Process each flow
    for (let i = 0; i < flows.length; i++) {
      const flow = flows[i];
      const flowName = flow.master_label || flow.label || flow.api_name || `Flow ${i + 1}`;
      
      // Update progress
      progressModal.updateProgress(i + 1, flows.length, flowName);
      if (onProgress) {
        onProgress(i + 1, flows.length, flowName);
      }

      try {
        // Get complete flow data with all related information
        console.log(`Loading complete data for flow: ${flowName}`);
        
        const { data: flowData, error: flowError } = await supabase
          .from('sf_flow_definitions')
          .select(`
            *,
            sf_flow_summaries(*),
            sf_flow_elements(*)
          `)
          .eq('id', flow.id)
          .single();
          
        if (flowError || !flowData) {
          throw new Error(`Failed to load flow data: ${flowName}`);
        }

        // Get performance scorecard if available
        let performanceScorecard = null;
        if (flowData.performance_calculated_at) {
          try {
            const { data: scorecard } = await supabase
              .from('performance_scorecards')
              .select('*')
              .eq('flow_id', flow.id)
              .order('created_at', { ascending: false })
              .limit(1)
              .single();
            
            if (scorecard) {
              performanceScorecard = scorecard;
            }
          } catch (err) {
            console.log('No performance scorecard found for flow');
          }
        }

        // Build HTML content for the flow
        const htmlContent = buildFlowHTML(flowData, workspaces, orgData, performanceScorecard);
        renderContainer.innerHTML = htmlContent;

        // Wait for content to render
        await new Promise(resolve => setTimeout(resolve, 500));

        // Generate canvas from the rendered content
        const canvas = await html2canvas(renderContainer, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          windowWidth: 1200,
          windowHeight: renderContainer.scrollHeight
        });

        // Add page break if not first page
        if (!isFirstPage) {
          pdf.addPage();
        } else {
          isFirstPage = false;
        }

        // Add flow to PDF
        const imgWidth = 190; // mm (leaving margins)
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const pageHeight = 260; // mm (leaving margins)
        
        // If content is taller than one page, handle multiple pages
        if (imgHeight > pageHeight) {
          let position = 0;
          let remainingHeight = imgHeight;
          
          while (remainingHeight > 0) {
            if (position !== 0) {
              pdf.addPage();
            }
            
            const imgData = canvas.toDataURL('image/jpeg', quality);
            pdf.addImage(imgData, 'JPEG', 10, 10 - position, imgWidth, imgHeight);
            
            remainingHeight -= pageHeight;
            position += pageHeight;
          }
        } else {
          // Content fits on one page
          const imgData = canvas.toDataURL('image/jpeg', quality);
          pdf.addImage(imgData, 'JPEG', 10, 10, imgWidth, imgHeight);
        }

      } catch (error) {
        console.error(`Error processing flow ${flowName}:`, error);
        
        // Add an error page for this flow
        if (!isFirstPage) {
          pdf.addPage();
        } else {
          isFirstPage = false;
        }
        
        pdf.setFontSize(16);
        pdf.text(`Error loading flow: ${flowName}`, 20, 30);
        pdf.setFontSize(12);
        pdf.text(error.message || 'Unknown error', 20, 40);
      }
    }

    // Generate filename with date
    const date = includeDate ? `-${new Date().toISOString().split('T')[0]}` : '';
    const fullFilename = `${filename}${date}.pdf`;

    // Save the PDF
    pdf.save(fullFilename);

    // Show success message
    progressModal.showSuccess(flows.length);
    
  } catch (error) {
    console.error('Multi-flow PDF export error:', error);
    progressModal.showError(error.message || 'Failed to generate PDF');
    throw error;
    
  } finally {
    // Clean up
    if (renderContainer.parentNode) {
      document.body.removeChild(renderContainer);
    }
    
    // Remove progress modal after a delay
    setTimeout(() => {
      if (progressModal.container.parentNode) {
        document.body.removeChild(progressModal.container);
      }
    }, 2000);
  }
}

/**
 * Build HTML content for a flow
 */
function buildFlowHTML(flow: any, workspaces: any[], orgData: any, performanceScorecard: any): string {
  // Find default workspace
  const defaultWorkspace = workspaces.find(w => w.org_id === flow.org_id && w.is_active && w.is_default);
  
  // Get summary for default workspace
  let summaryContent = '';
  if (defaultWorkspace && flow.sf_flow_summaries) {
    const workspaceSummary = flow.sf_flow_summaries.find((s: any) => s.workspace_id === defaultWorkspace.id);
    if (workspaceSummary) {
      summaryContent = workspaceSummary.ai_summary || workspaceSummary.content || '';
    }
  }

  // Get grade color
  const getGradeColor = (grade: string) => {
    switch(grade) {
      case 'A': return '#10b981';
      case 'B': return '#3b82f6';
      case 'C': return '#f59e0b';
      case 'D': return '#f97316';
      case 'F': case 'I': return '#ef4444';
      default: return '#6b7280';
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    return status === 'Active' ? '#10b981' : '#f59e0b';
  };

  // Group flow elements by type
  const elementsByType: Record<string, any[]> = {};
  if (flow.sf_flow_elements) {
    flow.sf_flow_elements.forEach((element: any) => {
      const type = element.element_type || 'Unknown';
      if (!elementsByType[type]) {
        elementsByType[type] = [];
      }
      elementsByType[type].push(element);
    });
  }

  return `
    <div style="background: white; padding: 40px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1f2937;">
      <!-- Header -->
      <div style="border-bottom: 3px solid #3182ce; padding-bottom: 20px; margin-bottom: 30px;">
        <div style="font-weight: 700; color: #3182ce; font-size: 16px; margin-bottom: 10px;">FlowDocs</div>
        <h1 style="margin: 0 0 10px 0; color: #1a202c; font-size: 28px; font-weight: 700;">
          ${flow.master_label || flow.label || flow.api_name || 'Flow'}
        </h1>
        <div style="color: #4a5568; font-size: 14px; margin: 5px 0;">
          <strong>API Name:</strong> ${flow.api_name || 'N/A'} | 
          <strong>Type:</strong> ${flow.process_type || 'Unknown'}
        </div>
        <div style="margin-top: 10px;">
          ${flow.namespace_prefix ? `<span style="display: inline-block; padding: 4px 12px; font-size: 12px; font-weight: 600; border-radius: 9999px; background-color: #dbeafe; color: #1e40af; margin-right: 8px;">${flow.namespace_prefix}</span>` : ''}
          <span style="display: inline-block; padding: 4px 12px; font-size: 12px; font-weight: 600; border-radius: 9999px; background-color: #dbeafe; color: #1e40af; margin-right: 8px;">
            ${flow.process_type || 'Unknown Type'}
          </span>
          <span style="display: inline-block; padding: 4px 12px; font-size: 12px; font-weight: 600; border-radius: 9999px; background-color: ${flow.status === 'Active' ? '#d1fae5' : '#fef3c7'}; color: ${flow.status === 'Active' ? '#065f46' : '#92400e'};">
            ${flow.status || 'Unknown'}
          </span>
          ${flow.performance_grade ? `
            <span style="display: inline-block; padding: 4px 12px; font-size: 12px; font-weight: 600; border-radius: 9999px; background-color: ${getGradeColor(flow.performance_grade)}; color: white;">
              Grade: ${flow.performance_grade}
            </span>
          ` : ''}
        </div>
      </div>

      ${flow.description ? `
        <section style="margin-bottom: 30px;">
          <h2 style="color: #2d3748; font-size: 20px; font-weight: 600; margin: 0 0 15px 0; padding-bottom: 8px; border-bottom: 2px solid #e2e8f0;">
            Description
          </h2>
          <p style="margin: 0; line-height: 1.7; color: #4a5568;">
            ${flow.description}
          </p>
        </section>
      ` : ''}

      ${summaryContent ? `
        <section style="margin-bottom: 30px;">
          <h2 style="color: #2d3748; font-size: 20px; font-weight: 600; margin: 0 0 15px 0; padding-bottom: 8px; border-bottom: 2px solid #e2e8f0;">
            AI Analysis
          </h2>
          <div style="background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px;">
            ${defaultWorkspace ? `<h3 style="color: #4a5568; font-size: 16px; font-weight: 600; margin: 0 0 10px 0;">${defaultWorkspace.name}</h3>` : ''}
            <div style="line-height: 1.8; color: #2d3748; white-space: pre-wrap;">
              ${summaryContent.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br>')}
            </div>
          </div>
        </section>
      ` : ''}

      ${flow.performance_grade || performanceScorecard ? `
        <section style="margin-bottom: 30px;">
          <h2 style="color: #2d3748; font-size: 20px; font-weight: 600; margin: 0 0 15px 0; padding-bottom: 8px; border-bottom: 2px solid #e2e8f0;">
            Performance Analysis
          </h2>
          
          ${flow.performance_grade ? `
            <div style="display: flex; align-items: center; margin-bottom: 20px;">
              <div style="display: inline-flex; align-items: center; justify-content: center; width: 60px; height: 60px; border-radius: 50%; font-size: 24px; font-weight: 700; color: white; background-color: ${getGradeColor(flow.performance_grade)}; margin-right: 15px;">
                ${flow.performance_grade}
              </div>
              <div>
                <h3 style="margin: 0; color: #2d3748; font-size: 18px;">Overall Performance Score</h3>
                ${flow.performance_score !== null ? `
                  <p style="margin: 4px 0; font-size: 24px; font-weight: 600; color: #4a5568;">
                    ${flow.performance_score}/100
                  </p>
                ` : ''}
                ${flow.performance_summary ? `
                  <p style="margin: 4px 0; color: #718096;">
                    ${flow.performance_summary}
                  </p>
                ` : ''}
              </div>
            </div>
          ` : ''}

          ${performanceScorecard ? `
            <div style="background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; margin-bottom: 20px;">
              <h4 style="color: #4a5568; font-size: 16px; font-weight: 600; margin: 0 0 12px 0;">Performance Metrics</h4>
              <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px;">
                <div style="background: white; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px;">
                  <div style="font-size: 12px; color: #718096; text-transform: uppercase; margin-bottom: 5px;">Total Queries</div>
                  <div style="font-size: 18px; font-weight: 600; color: #2d3748;">${performanceScorecard.details?.totalQueries || 0}</div>
                </div>
                <div style="background: white; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px;">
                  <div style="font-size: 12px; color: #718096; text-transform: uppercase; margin-bottom: 5px;">Queries in Loops</div>
                  <div style="font-size: 18px; font-weight: 600; color: ${performanceScorecard.details?.queriesInLoops > 0 ? '#DC2626' : '#2d3748'};">
                    ${performanceScorecard.details?.queriesInLoops || 0}
                  </div>
                </div>
                <div style="background: white; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px;">
                  <div style="font-size: 12px; color: #718096; text-transform: uppercase; margin-bottom: 5px;">Total DML Operations</div>
                  <div style="font-size: 18px; font-weight: 600; color: #2d3748;">${performanceScorecard.details?.totalDmlOperations || 0}</div>
                </div>
                <div style="background: white; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px;">
                  <div style="font-size: 12px; color: #718096; text-transform: uppercase; margin-bottom: 5px;">DML in Loops</div>
                  <div style="font-size: 18px; font-weight: 600; color: ${performanceScorecard.details?.dmlInLoops > 0 ? '#DC2626' : '#2d3748'};">
                    ${performanceScorecard.details?.dmlInLoops || 0}
                  </div>
                </div>
              </div>
            </div>
          ` : ''}
        </section>
      ` : ''}

      ${flow.trigger_info ? `
        <section style="margin-bottom: 30px;">
          <h2 style="color: #2d3748; font-size: 20px; font-weight: 600; margin: 0 0 15px 0; padding-bottom: 8px; border-bottom: 2px solid #e2e8f0;">
            Trigger Information
          </h2>
          <div style="background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px;">
            ${flow.trigger_info.triggerType ? `<p style="margin: 4px 0;"><strong>Trigger Type:</strong> ${flow.trigger_info.triggerType}</p>` : ''}
            ${flow.trigger_info.objectType ? `<p style="margin: 4px 0;"><strong>Object:</strong> ${flow.trigger_info.objectType}</p>` : ''}
            ${flow.trigger_info.recordTriggerType ? `<p style="margin: 4px 0;"><strong>When:</strong> ${flow.trigger_info.recordTriggerType}</p>` : ''}
            ${flow.trigger_info.conditionsDescription ? `<p style="margin: 4px 0;"><strong>Conditions:</strong> ${flow.trigger_info.conditionsDescription}</p>` : ''}
          </div>
        </section>
      ` : ''}

      ${Object.keys(elementsByType).length > 0 ? `
        <section style="margin-bottom: 30px;">
          <h2 style="color: #2d3748; font-size: 20px; font-weight: 600; margin: 0 0 15px 0; padding-bottom: 8px; border-bottom: 2px solid #e2e8f0;">
            Flow Elements (${flow.sf_flow_elements?.length || 0} total)
          </h2>
          ${Object.entries(elementsByType).map(([type, elements]) => `
            <div style="background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; margin-bottom: 12px;">
              <h3 style="color: #4a5568; font-size: 16px; font-weight: 600; margin: 0 0 10px 0;">
                ${type} (${elements.length})
              </h3>
              <ul style="margin: 0; padding-left: 20px;">
                ${elements.slice(0, 10).map((el: any) => `
                  <li style="margin: 6px 0; color: #4a5568;">
                    ${el.name || el.element_name || 'Unnamed'}${el.label && el.label !== el.name ? ` (${el.label})` : ''}
                  </li>
                `).join('')}
                ${elements.length > 10 ? `<li style="margin: 6px 0; color: #4a5568; font-style: italic;">... and ${elements.length - 10} more</li>` : ''}
              </ul>
            </div>
          `).join('')}
        </section>
      ` : ''}

      <section style="margin-bottom: 30px;">
        <h2 style="color: #2d3748; font-size: 20px; font-weight: 600; margin: 0 0 15px 0; padding-bottom: 8px; border-bottom: 2px solid #e2e8f0;">
          Flow Metadata
        </h2>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px;">
          <div style="background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px;">
            <div style="font-size: 12px; color: #718096; text-transform: uppercase; margin-bottom: 5px;">API Name</div>
            <div style="font-size: 16px; font-weight: 600; color: #2d3748;">${flow.api_name || 'N/A'}</div>
          </div>
          <div style="background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px;">
            <div style="font-size: 12px; color: #718096; text-transform: uppercase; margin-bottom: 5px;">Version</div>
            <div style="font-size: 16px; font-weight: 600; color: #2d3748;">${flow.version_number || flow.metadata?.activeVersionNumber || 'N/A'}</div>
          </div>
          <div style="background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px;">
            <div style="font-size: 12px; color: #718096; text-transform: uppercase; margin-bottom: 5px;">Last Modified</div>
            <div style="font-size: 16px; font-weight: 600; color: #2d3748;">
              ${flow.last_modified_date ? new Date(flow.last_modified_date).toLocaleDateString() : 'N/A'}
            </div>
          </div>
          <div style="background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px;">
            <div style="font-size: 12px; color: #718096; text-transform: uppercase; margin-bottom: 5px;">Modified By</div>
            <div style="font-size: 16px; font-weight: 600; color: #2d3748;">${flow.last_modified_by || 'Unknown'}</div>
          </div>
        </div>
      </section>

      <div style="margin-top: 50px; padding-top: 20px; border-top: 2px solid #e2e8f0; text-align: center; color: #718096; font-size: 12px;">
        <p>Generated by FlowDocs • ${new Date().toLocaleDateString()}</p>
      </div>
    </div>
  `;
}

/**
 * Create a progress modal for the export process
 */
function createProgressModal() {
  const container = document.createElement('div');
  container.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
  `;

  const modal = document.createElement('div');
  modal.style.cssText = `
    background: white;
    padding: 32px;
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    text-align: center;
    min-width: 400px;
  `;

  const spinner = document.createElement('div');
  spinner.innerHTML = `
    <svg class="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" style="animation: spin 1s linear infinite;">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
    <style>
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    </style>
  `;

  const title = document.createElement('h3');
  title.style.cssText = 'font-size: 20px; font-weight: 600; margin-bottom: 8px; color: #1f2937;';
  title.textContent = 'Generating PDF';

  const progressText = document.createElement('p');
  progressText.style.cssText = 'font-size: 16px; color: #6b7280; margin-bottom: 16px;';
  progressText.textContent = 'Processing flows...';

  const flowName = document.createElement('p');
  flowName.style.cssText = 'font-size: 14px; color: #9ca3af; font-style: italic;';
  flowName.textContent = '';

  modal.appendChild(spinner);
  modal.appendChild(title);
  modal.appendChild(progressText);
  modal.appendChild(flowName);
  container.appendChild(modal);

  return {
    container,
    updateProgress: (current: number, total: number, currentFlowName: string) => {
      progressText.textContent = `Processing flow ${current} of ${total}`;
      flowName.textContent = currentFlowName;
    },
    showSuccess: (total: number) => {
      spinner.innerHTML = `
        <svg class="h-12 w-12 text-green-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
      `;
      title.textContent = 'PDF Generated Successfully';
      progressText.textContent = `${total} flows exported`;
      flowName.textContent = 'Download should start automatically';
    },
    showError: (message: string) => {
      spinner.innerHTML = `
        <svg class="h-12 w-12 text-red-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
      `;
      title.textContent = 'Export Failed';
      progressText.textContent = message;
      flowName.textContent = '';
    }
  };
}